import { supabase } from "../../../lib/supabase";
import { BOOKING_INSIGHT_SELECT, type BookingInsight } from "./botAdminShared";
import { fetchBotLeanCustoms } from "./fetchBotLeanCustoms";
import { fetchBotMasterContainer } from "./fetchBotMasterContainer";

/** Attach last-mile + timeline + LeanCustoms + master B/L extras for bot context. */
export async function enrichBotBooking(
  booking: BookingInsight,
): Promise<BookingInsight> {
  const { data: shipments, error: shipmentsError } = await supabase
    .from("sendcloud_shipments")
    .select(
      `
      id, status, carrier_name, tracking_number, created_at,
      sendcloud_tracking_events(
        id, event_timestamp, status, message, location
      )
    `,
    )
    .eq("booking_id", booking.id)
    .order("created_at", { ascending: false });

  if (shipmentsError) {
    console.warn("Sendcloud fetch for bot insights:", shipmentsError);
  }

  const lastMile =
    (shipments ?? []).map((shipment) => {
      const events = Array.isArray(shipment.sendcloud_tracking_events)
        ? [...shipment.sendcloud_tracking_events].sort(
            (a, b) =>
              new Date(b.event_timestamp).getTime() -
              new Date(a.event_timestamp).getTime(),
          )
        : [];
      return {
        id: shipment.id,
        status: shipment.status,
        carrier_name: shipment.carrier_name,
        tracking_number: shipment.tracking_number,
        created_at: shipment.created_at,
        latest_event: events[0]
          ? {
              status: events[0].status,
              message: events[0].message,
              location: events[0].location,
              event_timestamp: events[0].event_timestamp,
            }
          : null,
        tracking_events: events,
      };
    }) ?? [];

  const [
    { data: comments },
    { data: notifications },
    leanCustoms,
    masterContainer,
  ] = await Promise.all([
    supabase
      .from("booking_comment")
      .select(
        `
        id, description, created_at,
        comment_types!comments_comment_type_fkey ( id, name )
      `,
      )
      .eq("booking_id", booking.id)
      .order("created_at", { ascending: false })
      .limit(50),
    supabase
      .from("notifications")
      .select("id, created_at, change_data")
      .eq("booking_id", booking.id)
      .order("created_at", { ascending: false })
      .limit(50),
    fetchBotLeanCustoms(booking),
    fetchBotMasterContainer(booking),
  ]);

  return {
    ...booking,
    last_mile_shipments: lastMile,
    timeline: {
      comments: comments ?? [],
      notifications: notifications ?? [],
    },
    lean_customs: leanCustoms,
    master_container: masterContainer,
  };
}

/** Load full insight row by id, scoped to company. */
export async function fetchEnrichedBookingById(
  bookingId: number,
  companyId: number,
): Promise<BookingInsight | null> {
  const { data, error } = await supabase
    .from("bookings")
    .select(BOOKING_INSIGHT_SELECT)
    .eq("id", bookingId)
    .eq("company_id", companyId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;
  return enrichBotBooking(data as unknown as BookingInsight);
}
